import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useContext } from 'react';
import { getProductGroups, addProductGroupAddOn } from "../../api/api";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";
import Avatar from 'react-avatar';
import {
    Button,
    Col,
    Form,
    Input,
    Modal,
    ModalBody,
    ModalHeader,
    Row
} from "reactstrap";
import Loader from "../../components/Loader";
import RecordsNotFound from "../../components/RecordsNotFound";
import { Link } from "react-router-dom";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import Paginator from "../../components/Paginator";

function ProductGroupItemList(props) {
    const { refreshToken } = useContext(AuthContext);
    const [page, setPage] = useState(1);
    const [productName, setProductName] = useState("");
    const [queryKey, setQueryKey] = useState(['productGroups', page]);
    const queryClient = useQueryClient();
    const [selectedProductGroup, setSelectedProductGroup] = useState(null);
    const [selectedAddOns, setSelectedAddOns] = useState([]);
    const records = 9;

    useEffect(() => {
        setQueryKey(['productGroups', page]);
    }, [page]);

    const { status, data, error, isFetching, refetch } = useQuery({
        queryKey: queryKey,
        queryFn: () => getProductGroups(page,'', productName, '', '', "ACTIVE", '', '', '', '', records),
        keepPreviousData: true,
        retry: false,
        onError: (err) => {
            handleUnauthorized401QueryError(err, refreshToken, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        },
    });

    const productGroupAddOnMutation = useMutation({
        mutationFn: addProductGroupAddOn,
        onSuccess: () => {
            queryClient.invalidateQueries(['cart']);
            if (!toast.isActive('groupAddOnAddedToast')) {
                toast.success('Product Group Add-On Added!', { toastId: 'groupAddOnAddedToast' });
            }
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, productGroupAddOnMutation, variables);
            let errorMessage = err?.response?.data?.detail || 'An unexpected error occurred.';

            if (err.response && err.response.data) {
                const customerErrors = err.response.data['customer'];
                const nonFieldErrors = err.response.data.non_field_errors;

                if (err.response.status === 400 && (customerErrors && (customerErrors[0] === "Object with email=undefined does not exist." || customerErrors[0] === "This field may not be null."))) {
                    errorMessage = 'Please select a customer!';
                } else if (nonFieldErrors && nonFieldErrors.length > 0) {
                    errorMessage = nonFieldErrors[0];
                }
            }

            toast.error(errorMessage);
        }
    });

    const handleProductGroupClick = (e, productGroup) => {
        if (productGroup.add_ons_supported && productGroup.add_ons_supported.count !== 0) {
            setSelectedProductGroup(productGroup);
        } else {
            const formData = new FormData();
            formData.append("product_group", productGroup.id);
            formData.append("customer", props.customer);
            productGroupAddOnMutation.mutate(formData);
            closeModal();
        }
    };

    const handleAddOnProductSubmit = (e, productGroup, addOns = []) => {
        const formData = new FormData();
        formData.append("product_group", productGroup.id);
        formData.append("customer", props.customer);
        if (addOns.length > 0) {
            addOns.forEach(addOnId => {
                formData.append("add_on_products", addOnId);
            });
        }
        productGroupAddOnMutation.mutate(formData);
        closeModal();
    }

    const handleProductSearch = (e, name) => {
        e?.preventDefault();
        setPage(1);
        setProductName(name);
        setQueryKey(['productGroups', page, name]);
    };

    const handleAddOnClick = (addOnProduct) => {
        setSelectedAddOns(prevSelected => {
            if (prevSelected.includes(addOnProduct.id)) {
                return prevSelected.filter(id => id !== addOnProduct.id);
            } else {
                return [...prevSelected, addOnProduct.id];
            }
        });
    };

    const isAddOnSelected = (addOnProduct) => selectedAddOns.includes(addOnProduct.id);

    const closeModal = () => {
        setSelectedProductGroup(null);
        setSelectedAddOns([]);
    };

    const uniqueAddOns = selectedProductGroup?.add_ons_supported?.add_on_products
        ?.filter((value, index, self) =>
                index === self.findIndex((t) => (
                    t.id === value.id
                ))
        );

    return (
        <>
            {status === 'loading' && <Loader size={'default'} />}

            {status === 'success' | error?.response?.status === 401 && (
                <>
                    <div className="row">
                        <div className="col-sm-12 mb-2">
                            <Form onSubmit={(e) => handleProductSearch(e, productName)}>
                                <Row className="form-row align-items-center">
                                    <Col>
                                        <Input
                                            id="name"
                                            name="name"
                                            type="text"
                                            placeholder="Search Product Groups..."
                                            className="form-control mb-2"
                                            onChange={(e) => handleProductSearch(e, e.target.value)}
                                            value={productName}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        </div>

                        {data?.count > 0 ? data?.results?.map(productGroup => (
                            <div className="col-lg-4" key={productGroup.id}>
                                <Link>
                                    <div
                                        className={`card ribbon-box ${productGroup.products.some(product => product.stock === 0) ? 'out-of-stock' : ''}`}
                                        style={{ background: "#202027", pointerEvents: productGroup.products.some(product => product.stock === 0) ? 'none' : 'auto' }}
                                        onClick={(e) => handleProductGroupClick(e, productGroup)}
                                    >
                                        {productGroup.products.some(product => product.stock === 0) ? (
                                            <div className="ribbon-two ribbon-two-danger">
                                                <span className="stock-banner">Out of Stock</span>
                                            </div>
                                        ) : (
                                            productGroup.add_ons_supported && productGroup.add_ons_supported.count > 0 && (
                                                <div className="ribbon-two ribbon-two-secondary">
                                                    <span>Add-On</span>
                                                </div>
                                            )
                                        )}
                                        <div className="box-details">
                                            {productGroup.image ?
                                                <img src={productGroup.image} className="card-img-top img-fluid box-image" alt="product group" />
                                                :
                                                <Avatar name={productGroup.name} size="205" round={false} className="list-avatar"/>
                                            }
                                        </div>
                                        <div className="card-body" style={{ background: "#202027" }}>
                                            <h5 className="card-title" data-toggle="tooltip" data-placement="top" title={productGroup.name}>
                                                {productGroup.name.length > 15 ? `${productGroup.name.slice(0, 15)}...` : productGroup.name}
                                            </h5>
                                            <p className="card-text">INR {productGroup.price}</p>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        )) : <RecordsNotFound />}
                        </div>

                    {selectedProductGroup && (
                        <Modal className={'modal-lg'} isOpen={true} toggle={closeModal}>
                            <ModalHeader toggle={closeModal} close={
                                <button className="close" onClick={closeModal}>
                                    &times;
                                </button>
                            }>Product Group details: {selectedProductGroup.name}</ModalHeader>
                            <ModalBody>
                                <Row>
                                    {uniqueAddOns.map(addOnProduct => (
                                        <Col lg="4" key={addOnProduct.id}>
                                            <Link>
                                                <div
                                                    className={`card ribbon-box ${isAddOnSelected(addOnProduct) ? 'border border-primary' : ''} ${addOnProduct.stock === 0 ? 'out-of-stock' : ''}`}
                                                    style={{ background: "#202027", pointerEvents: addOnProduct.stock === 0 ? 'none' : 'auto' }}
                                                    onClick={() => handleAddOnClick(addOnProduct)}
                                                >
                                                    {addOnProduct.stock === 0 && (
                                                        <div className="ribbon-two ribbon-two-danger">
                                                            <span className="stock-banner">Out of Stock</span>
                                                        </div>
                                                    )}
                                                    <div className="box-details">
                                                    {addOnProduct.image ? <img src={addOnProduct.image} className="card-img-top img-fluid box-image" alt="add-on" />
                                                        : <Avatar name={addOnProduct.name} size="240" round={false} className="addon-avatar"/>}
                                                    </div>
                                                    <div className="card-body" style={{ background: "#202027" }}>
                                                        <h5 className="card-title" data-toggle="tooltip" data-placement="top" title={addOnProduct.name}>{addOnProduct.name}</h5>
                                                        <p className="card-text">INR {addOnProduct.price}</p>
                                                    </div>
                                                </div>
                                            </Link>
                                        </Col>
                                    ))}
                                </Row>
                                <div className="mt-3 text-md-right">
                                    <Button onClick={(e) => handleAddOnProductSubmit(e, selectedProductGroup, selectedAddOns)} color="primary" disabled={productGroupAddOnMutation.isLoading}>{productGroupAddOnMutation.isLoading? <><Loader size={'small'}/>Adding to Cart...</>: `Add to Cart`}</Button>
                                </div>
                            </ModalBody>

                        </Modal>
                    )}

                    <Paginator data={data} page={page} setPage={setPage} last={data?.last_page} loading={isFetching}/>
                </>
            )}
        </>
    );
}

export default ProductGroupItemList;
